import { css } from "@emotion/react";
import { useMemo } from "react";
import * as React from "react";
import { connect, useSelector } from "react-redux";
import { RowInfo } from "react-table";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { IStoreState } from "interfaces/storeState";
import { ReportLocalTimeLabel } from "routes/reports/components/ReportLocaltimeLabel";
import { ISolvedAndClosedStats } from "services/api/report/interfaces";
import { IAgent, selectAgents } from "store/entities/agents";
import { selectFeatureTogglePerformanceReport } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { ITeam, selectTeams } from "store/entities/teams";
import {
  ISolvedClosedTicketsStats,
  getDistribution,
  getFilterDateRange,
  getIsReportFetching,
  getReportData,
} from "store/views/reports";
import { withVar } from "themes/helpers";

import { ChartPanel, Filters } from "../components";
import { ChartBreakdown } from "../components/ChartBreakdown";
import { AgentCell, TeamCell, ValueCell } from "../components/PerformanceTableCells";
import { PerformanceTablePromo } from "../components/PerformanceTablePromo";
import { getChartOptions, getExportFilename } from "../helpers";
import { containerFluid } from "../styled/layout";

interface IStateProps {
  isFetching: boolean;
  data: ISolvedClosedTicketsStats | undefined;
  distribution: ReportDistribution;
  dateRange: IDateRangeWithCompareFilter;
  agents: Record<string, IAgent>;
  teams: Record<string, ITeam>;
  isTeam: boolean;
}

type IProps = IStateProps;

const SolvedTickets: React.FC<IProps> = React.memo(
  ({ isFetching, data, distribution, dateRange, teams, agents, isTeam }) => {
    SolvedTickets.displayName = "SolvedTickets";
    useLastVisitedReport(ReportType.SolvedAndClosed);
    const hasPerformanceReport = useSelector(selectFeatureTogglePerformanceReport);
    const solvedTicketsChartLegend = useMemo(() => data && data.solvedClosedTickets.summary, [data]);
    const perTeamData = useMemo(() => data && data.solvedAndClosedTicketsPerTeam, [data]);
    const perAgentData = useMemo(() => data && data.solvedAndClosedTicketsPerAgent, [data]);
    const perTeamTotalRow = perTeamData?.find(item => item.id === "total");
    const perAgentTotalRow = perAgentData?.find(item => item.id === "total");
    const avgSolvedPerAgent = perAgentTotalRow?.solved;
    const avgClosedPerAgent = perAgentTotalRow?.closed;
    const avgSolvedPerTeam = perTeamTotalRow?.solved;
    const avgClosedPerTeam = perTeamTotalRow?.closed;
    const avgTotalSolvedAndClosedPerTeam = perTeamTotalRow?.total;
    const avgTotalSolvedAndClosedPerAgent = perAgentTotalRow?.total;
    const solvedTicketsChartOptions = useMemo(
      () =>
        data &&
        getChartOptions(data.solvedClosedTickets.series, data.solvedClosedTickets.labels, distribution, null, {
          forceOptions: {
            fixedSeriesHeader: "Status",
          },
        }),
      [data, distribution]
    );

    const teamColumns = useMemo(() => {
      return [
        {
          Header: "Name",
          accessor: "id",
          Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => (
            <TeamCell value={value} row={row} teamName={teams[value]?.name} totalLabel="Average" />
          ),
          className: "even",
        },
        {
          Header: (
            <>
              <span css={tableSolvedHeaderStyles}></span>
              <span>Solved</span>
            </>
          ),
          accessor: "solved",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell value={value} valueToCompare={avgSolvedPerTeam} original={original} formatAsTime={false} />
          ),
          className: "odd",
        },
        {
          Header: (
            <>
              <span css={tableClosedHeaderStyles}></span>
              <span>Closed</span>
            </>
          ),
          accessor: "closed",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell value={value} valueToCompare={avgClosedPerTeam} original={original} formatAsTime={false} />
          ),
          className: "even",
        },
        {
          Header: <strong>Overall</strong>,
          accessor: "total",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell
              value={value}
              valueToCompare={avgTotalSolvedAndClosedPerTeam}
              original={original}
              formatAsTime={false}
            />
          ),
          className: "odd",
        },
      ];
    }, [data, teams]);

    const agentColumns = useMemo(() => {
      return [
        {
          Header: "Name",
          accessor: "id",
          Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => {
            const agent = agents[value];
            const { avatar, name } = agent ?? {};
            return <AgentCell value={value} row={row} agentName={name} avatar={avatar} totalLabel="Average" />;
          },
          className: "even",
        },
        {
          Header: (
            <>
              <span css={tableSolvedHeaderStyles}></span>
              <span>Solved</span>
            </>
          ),
          accessor: "solved",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell value={value} valueToCompare={avgSolvedPerAgent} original={original} formatAsTime={false} />
          ),
          className: "odd",
        },
        {
          Header: (
            <>
              <span css={tableClosedHeaderStyles}></span>
              <span>Closed</span>
            </>
          ),
          accessor: "closed",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell value={value} valueToCompare={avgClosedPerAgent} original={original} formatAsTime={false} />
          ),
          className: "even",
        },
        {
          Header: <strong>Overall</strong>,
          accessor: "total",
          Cell: ({ value, original }: { value: number; original: ISolvedAndClosedStats }): JSX.Element => (
            <ValueCell
              value={value}
              valueToCompare={avgTotalSolvedAndClosedPerAgent}
              original={original}
              formatAsTime={false}
            />
          ),
          className: "odd",
        },
      ];
    }, [data, teams]);

    return (
      <div>
        <Filters />
        <div css={containerFluid}>
          <ChartPanel
            title="Solved and closed"
            tooltip="The number of tickets solved or closed by agents in a given time frame."
            loading={isFetching}
            legend={solvedTicketsChartLegend}
            options={solvedTicketsChartOptions}
            exportFileName={getExportFilename("solved_tickets", dateRange, distribution)}
            customComponent={
              hasPerformanceReport ? (
                <>
                  <ChartBreakdown
                    sort={{ id: "total", desc: true }}
                    customTitle="Solved and closed per team"
                    breakdownData={{ columns: teamColumns, data: perTeamData }}
                    loading={isFetching}
                    truncateData={perTeamData && perTeamData.length > 5}
                  />
                  <Block marginBottom={withVar(SpacingToken.Spacing7)} />
                  <ChartBreakdown
                    sort={{ id: "total", desc: true }}
                    fallbackComponent={isTeam && <PerformanceTablePromo />}
                    customTitle="Solved and closed per agent"
                    breakdownData={{ columns: agentColumns, data: perAgentData }}
                    loading={isFetching}
                    truncateData={perAgentData && perAgentData.length > 5}
                    disableExpand={isTeam}
                  />
                  <Block marginBottom={withVar(SpacingToken.Spacing7)} />
                </>
              ) : null
            }
          />
          <ReportLocalTimeLabel />
        </div>
      </div>
    );
  }
);

SolvedTickets.displayName = "SolvedTickets";

const mapStateToProps = (state: IStoreState): IStateProps => ({
  isFetching: getIsReportFetching(state, ReportType.SolvedAndClosed),
  data: getReportData(state, ReportType.SolvedAndClosed) as unknown as ISolvedClosedTicketsStats,
  distribution: getDistribution(state),
  dateRange: getFilterDateRange(state),
  agents: selectAgents(state),
  teams: selectTeams(state),
  isTeam: selectCurrentSubscriptionPlan(state) === Plan.Team,
});

export default connect(mapStateToProps, null)(SolvedTickets);

const tableHeaderStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: ${withVar(SpacingToken.Spacing1)};
`;

const tableSolvedHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorGreen500)};
`;
const tableClosedHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.ContentBasicSecondary)};
`;
